import { useSelector } from '@/libs/state'
import type { CompanyWorkflow, WorkItem, WorkItemPriority, WorkItemSize } from '../domain/workflow'
import { editingWorkItemId$, viewingWorkItemId$, workItems$ } from '../store'
import {
	cancelReason$,
	commentBody$,
	rejectReason$,
	resetWorkItemComposers,
	showArchive$,
	showCancel$,
	showReject$,
} from '../work-item-detail-store'
import { useMemberLookup } from './use-member-lookup'
import { useAllMembers } from './use-all-members'
import { useWorkItemLifecycle } from './use-work-item-lifecycle'
import { useUpdateWorkItem } from './use-update-work-item'
import { useWorkItemActorCan } from './use-work-item-actor-can'
import { useSpawns } from './use-spawns'

// View-model for a single work item: resolves the item and its related data,
// exposes the actor's allowed actions, and wraps the lifecycle/update use-cases.
// Dialog and composer state live in work-item-detail-store so the page can be
// split into focused components without threading state through props.
const useWorkItemDetail = (workflow: CompanyWorkflow, workItemId: string | null) => {
	const workItems = useSelector(workItems$)
	const lifecycle = useWorkItemLifecycle(workflow.phases)
	const { update } = useUpdateWorkItem()

	const item = workItemId ? (workItems.find(w => w.id === workItemId) ?? null) : null
	useSpawns(item?.id ?? null)
	const spawns = item ? workItems.filter(w => w.parentId === item.id) : []
	const openSpawnCount = spawns.filter(
		st => st.status !== 'completed' && st.status !== 'cancelled' && st.status !== 'archived',
	).length

	const phaseTeamId = item ? (workflow.phases.find(p => p.id === item.currentPhaseId)?.teamId ?? null) : null
	// Assignment is scoped to the phase team; name resolution spans all members,
	// since an assignee may have been a manager outside the current phase team.
	const { members } = useMemberLookup(phaseTeamId)
	const { memberNameById, memberAvatarById } = useAllMembers()

	const actorCan = useWorkItemActorCan(item, workflow.phases)

	const memberOptions = members.map(member => ({ id: member.id, label: member.name }))
	// assignedTo / history payloads store user IDs; resolve to a display name via
	// the loaded member list, falling back to the raw ID if the user is unknown.
	const memberName = (id: string | null): string => {
		if (!id) return '-'
		return memberNameById.get(id) ?? id
	}
	const selectedAssignee = item?.assignedTo ? { id: item.assignedTo, label: memberName(item.assignedTo) } : undefined

	const onAssign = (option: { id: string; label: string }) => {
		if (!item || !option.id) return
		lifecycle.assign(item, { id: option.id, name: option.label })
	}

	const onConfirmCancel = () => {
		const reason = cancelReason$.get().trim()
		if (!item || !reason) return
		lifecycle.cancel(item, reason).then(() => {
			showCancel$.set(false)
			cancelReason$.set('')
		})
	}

	const onConfirmReject = () => {
		const reason = rejectReason$.get().trim()
		if (!item || !reason) return
		lifecycle.reject(item, reason).then(() => {
			showReject$.set(false)
			rejectReason$.set('')
		})
	}

	const onConfirmArchive = () => {
		if (!item) return
		lifecycle.archive(item, openSpawnCount > 0).then(() => showArchive$.set(false))
	}

	const onComment = () => {
		const body = commentBody$.get().trim()
		if (!item || !body) return
		lifecycle.comment(item, body).then(() => commentBody$.set(''))
	}

	const openEdit = () => {
		if (!item) return
		viewingWorkItemId$.set(null)
		editingWorkItemId$.set(item.id)
	}

	const startWork = (target: WorkItem) => lifecycle.changeWorkState(target, 'in_progress')
	const pauseWork = (target: WorkItem) => lifecycle.changeWorkState(target, 'todo')

	const setTitle = (title: string) => {
		const next = title.trim()
		if (!item || !next || next === item.title) return
		update({ id: item.id, title: next })
	}

	const setSubject = (subjectName: string) => {
		const next = subjectName.trim()
		if (!item || !next || next === item.subjectName) return
		update({ id: item.id, subjectName: next })
	}

	const setExternalId = (externalId: string) => {
		const next = externalId.trim() || null
		if (!item || next === item.externalId) return
		update({ id: item.id, externalId: next })
	}

	const setNotes = (notes: string) => {
		const next = notes.trim() || null
		if (!item || next === item.notes) return
		update({ id: item.id, notes: next })
	}

	const setPriority = (priority: WorkItemPriority | null) => {
		if (!item || priority === item.priority) return
		update({ id: item.id, priority })
	}

	const setSize = (size: WorkItemSize | null) => {
		if (!item || size === item.size) return
		update({ id: item.id, size })
	}

	const setLabels = (labels: string[]) => {
		if (!item) return
		const same = labels.length === item.labels.length && labels.every((label, i) => label === item.labels[i])
		if (same) return
		update({ id: item.id, labels })
	}

	const setDueDate = (dueDate: string | null) => {
		if (!item || dueDate === item.dueDate) return
		update({ id: item.id, dueDate })
	}

	const setCustomer = (customer: { id: string; name: string } | null) => {
		if (!item) return
		if (customer?.id === item.customerId) return
		update({
			id: item.id,
			customerId: customer?.id ?? null,
			customerName: customer?.name ?? null,
		})
	}

	return {
		item,
		spawns,
		setTitle,
		setSubject,
		setNotes,
		setExternalId,
		openSpawnCount,
		members,
		memberOptions,
		memberName,
		memberAvatarById,
		selectedAssignee,
		actorCan,
		resetComposers: resetWorkItemComposers,
		onAssign,
		onConfirmCancel,
		onConfirmReject,
		onConfirmArchive,
		onComment,
		openEdit,
		startWork,
		pauseWork,
		setPriority,
		setSize,
		setLabels,
		setDueDate,
		setCustomer,
		accept: lifecycle.accept,
		reject: lifecycle.reject,
		complete: lifecycle.complete,
		reopen: lifecycle.reopen,
		conclude: lifecycle.conclude,
		setLightweight: lifecycle.setLightweight,
	}
}

export { useWorkItemDetail }
